import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';

export default function FAQPage() {
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleItem = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const faqData = [
    {
      question: "What is this portal and what does it do?",
      answer: "This is the Ministry of Education's Examination Results Portal. It provides students with access to view their examination results from various national level tests including exit examinations, NGAT, remedial exams, TVET, and TDP. The portal serves as a centralized platform for accessing examination results across different educational programs in Ethiopia."
    },
    {
      question: "How do I view my examination results?",
      answer: "To view your results, first select the examination category that applies to you (Exit Examination, NGAT, Remedial, TVET, or TDP) from the main page. Then enter your username in the provided field and click 'Submit' to retrieve your results. Your results will show your examination details, scores, and status."
    },
    {
      question: "What information do I need to access my results?",
      answer: "You need your username to access your results. This is the username that was provided to you during your examination registration. Make sure to enter it exactly as it was given to you. If you don't have your username, contact your educational institution or examination center."
    },
    {
      question: "How do I submit a complaint about my results?",
      answer: "If you need to submit a complaint, you can do so from your results page. Click the 'Submit Complaint' button, fill out the form with your details, select the appropriate complaint category, provide a brief description of your issue (limited to 150 characters), and include your phone number for contact purposes."
    },
    {
      question: "What should I do if my result shows as 'Disqualified'?",
      answer: "If your result shows as 'Disqualified', this indicates there was an issue with your examination that prevented a valid result from being recorded. This doesn't necessarily mean you need to submit a complaint - it could be due to various administrative or procedural reasons. However, if you believe there was an error or you have concerns about your disqualification, you can submit a complaint through the portal explaining your situation."
    },
    {
      question: "Can I download my results as a PDF?",
      answer: "PDF download functionality may be available for certain examination types. If available, you can download your results by clicking the 'Download PDF' button on your results page. The availability of PDF downloads depends on the examination type and current system capabilities."
    },
    {
      question: "What if I can't find my results or get an error message?",
      answer: "If you can't find your results or receive an error message, first check that you've entered your username correctly. If the problem persists, it could mean your results haven't been processed yet, there's an issue with your registration, or your username might be incorrect. In such cases, submit a complaint through the portal."
    },
    {
      question: "How long does it take to process complaints?",
      answer: "Complaint processing times vary depending on the nature of the issue and current workload. You will be contacted via the phone number you provide in your complaint form once your case has been reviewed. Please be patient as thorough investigation may be required."
    },
    {
      question: "Is my personal information secure on this portal?",
      answer: "Your personal information is protected on this portal. The system is operated by the Ministry of Education and follows established data protection procedures. Your results and personal information are only accessible to you using your unique username."
    },
    {
      question: "What should I do if I forgot my username?",
      answer: "If you've forgotten your username, contact your educational institution or the examination center where you registered. They will be able to help you retrieve your username or provide guidance on how to access your results. The portal cannot reset or recover usernames."
    },
    {
      question: "Are the results on this portal official?",
      answer: "Yes, the results displayed on this portal are official examination results from the Ministry of Education. These results are recognized by educational institutions and can be used for academic and professional purposes."
    },
    {
      question: "What if I notice an error in my displayed results?",
      answer: "If you notice any errors in your displayed results, such as incorrect personal information, wrong scores, or missing data, submit a complaint through the portal. Clearly describe the error you've identified and provide any supporting information you may have."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <i className="fa-solid fa-arrow-left mr-2" style={{ color: 'rgb(18,82,166)' }}></i>
            <span>Back to Home</span>
          </button>
        </div>

        {/* FAQ Header */}
        <section className="mb-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-4 flex items-center justify-center">
              <i className="fa-solid fa-question-circle mr-3" style={{ color: 'rgb(18,82,166)' }} />
              Frequently Asked Questions
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about the Examination Results Portal. 
              Click on any question to expand and view the detailed answer.
            </p>
          </div>
        </section>

        {/* FAQ Items */}
        <section className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqData.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800 pr-4">
                      {item.question}
                    </h3>
                    <i 
                      className={`fa-solid fa-chevron-down transition-transform duration-200 ${
                        expandedItems.has(index) ? 'rotate-180' : ''
                      }`}
                      style={{ color: 'rgb(18,82,166)' }}
                    />
                  </div>
                </button>
                
                {expandedItems.has(index) && (
                  <div className="px-6 pb-4 border-t border-gray-100">
                    <div className="pt-4">
                      <p className="text-gray-700 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section className="mt-12 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center justify-center">
                <i className="fa-solid fa-headset mr-3" style={{ color: 'rgb(18,82,166)' }} />
                Still Need Help?
              </h2>
              <p className="text-gray-600 mb-6">
                If you couldn't find the answer to your question above, please submit a complaint 
                through the portal or contact us via Telegram for immediate assistance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 text-white rounded-lg font-medium transition-colors duration-200"
                  style={{ backgroundColor: 'rgb(18,82,166)' }}
                >
                  <i className="fa-solid fa-home mr-2" />
                  Go to Home Page
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200"
                >
                  <i className="fa-solid fa-search mr-2" />
                  Check Results
                </button>
                <button
                  onClick={() => window.open('https://t.me/moeexitexamofficial', '_blank')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200"
                >
                  <i className="fa-brands fa-telegram mr-2" />
                  Contact Support
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
} 